// import { getReferenceExample, getInTextExample } from '../../../config/citation-config-new';
import {
	generateReference,
	generateInText,
	getOrganizationCitationParts
} from '../../../utils/citationGenerator';
import { getInTextExample, getReferenceExample } from '../../../config/citation-config-new';
import { getFrontendCategory } from '../../../utils/citationFieldMapper';

export default function CitationPreview({ selectedCategory, selectedSource, citationData }) {
	const textBreakStyle = {
		wordBreak: 'break-word',
		overflowWrap: 'anywhere'
	};

	const frontendCategory =
		getFrontendCategory({ category: selectedCategory, mainCategory: selectedCategory }) ||
		selectedCategory;

	const liveReference =
		frontendCategory && selectedSource && citationData
			? generateReference(frontendCategory, selectedSource, citationData)
			: '';

	const firstInText =
		frontendCategory && selectedSource && citationData
			? generateInText(frontendCategory, selectedSource, citationData, {
					isFirstOrganizationCite: true
				})
			: { parenthetical: '', narrative: '' };

	const subInText =
		frontendCategory && selectedSource && citationData
			? generateInText(frontendCategory, selectedSource, citationData, {
					isFirstOrganizationCite: false
				})
			: { parenthetical: '', narrative: '' };

	const orgAbbrevPreview =
		citationData?.AuthorOrOrganization === 'Organization' &&
		citationData?.organization &&
		getOrganizationCitationParts(citationData).hasAbbreviation;

	const hasUserData =
		citationData &&
		Object.keys(citationData).length > 0 &&
		Object.values(citationData).some((val) => {
			if (Array.isArray(val)) return val.some((v) => v && v.trim());
			return val && val.toString().trim();
		});

	const staticInTextExample =
		selectedCategory && selectedSource ? getInTextExample(selectedCategory, selectedSource) : '';
	const staticReferenceExample =
		selectedCategory && selectedSource ? getReferenceExample(selectedCategory, selectedSource) : '';

	const parseInTextExample = (example) => {
		if (!example) return { parenthetical: '', narrative: '' };
		const lines = example.split('\n').map((l) => l.trim());
		let parenthetical = '';
		let narrative = '';
		for (const line of lines) {
			if (line.toLowerCase().startsWith('parenthetical:')) {
				parenthetical = line.replace(/^parenthetical:\s*/i, '');
			} else if (line.toLowerCase().startsWith('narrative:')) {
				narrative = line.replace(/^narrative:\s*/i, '');
			}
		}
		return { parenthetical, narrative };
	};

	const exampleInText = parseInTextExample(staticInTextExample);

	const renderInTextLine = (label, value) => {
		if (!value) return null;
		return (
			<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
				<p className="text-sm text-dark-blue/60" style={textBreakStyle}>
					<span className="font-medium text-dark-blue/80">{label}:</span> {value}
				</p>
			</div>
		);
	};

	if (!selectedCategory || !selectedSource) {
		return (
			<div className="bg-gray-50 rounded-[20px] p-6">
				<div className="mb-4">
					<h3 className="text-[25px] font-bold text-dark-blue mb-2">Live Preview</h3>
					<p className="text-sm text-gray">
						Select a category and source type to preview your formatted citation.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-gray-50 rounded-[20px] p-6 pr-2">
			<div className="mb-4">
				<h3 className="text-[25px] font-bold text-dark-blue mb-2">Live Preview</h3>
				<p className="text-sm text-gray">
					{hasUserData ? 'Your citation updates as you type' : 'Start typing to see preview'}
				</p>
			</div>

			{hasUserData && (
				<div className="mb-6">
					<h4 className="text-sm font-medium text-dark-blue mb-2">In-text</h4>
					<div className="space-y-3">
						{orgAbbrevPreview ? (
							<>
								<div>
									<p className="text-xs font-semibold text-dark-blue/80 mb-1">First citation</p>
									<div className="space-y-1">
										{renderInTextLine('Parenthetical', firstInText.parenthetical)}
										{renderInTextLine('Narrative', firstInText.narrative)}
									</div>
								</div>
								<div>
									<p className="text-xs font-semibold text-dark-blue/80 mb-1">Subsequent citation</p>
									<div className="space-y-1">
										{renderInTextLine('Parenthetical', subInText.parenthetical)}
										{renderInTextLine('Narrative', subInText.narrative)}
									</div>
								</div>
							</>
						) : (
							<div className="space-y-1">
								{renderInTextLine('Parenthetical', firstInText.parenthetical)}
								{renderInTextLine('Narrative', firstInText.narrative)}
							</div>
						)}
					</div>
				</div>
			)}

			{!hasUserData && (exampleInText.parenthetical || exampleInText.narrative) && (
				<div className="mb-6">
					<h4 className="text-sm font-medium text-dark-blue mb-2">In-text example</h4>
					<div className="space-y-1">
						{renderInTextLine('Parenthetical', exampleInText.parenthetical)}
						{renderInTextLine('Narrative', exampleInText.narrative)}
					</div>
				</div>
			)}

			{hasUserData && liveReference && (
				<div className="my-6">
					<h4 className="text-sm font-medium text-dark-blue mb-2">Reference entry</h4>
					<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
						<p
							className="text-sm text-dark-blue/60 leading-relaxed whitespace-pre-line"
							style={textBreakStyle}
							dangerouslySetInnerHTML={{
								__html: liveReference
							}}
						/>
					</div>
				</div>
			)}

			{hasUserData && citationData.notes?.trim() && (
				<div className="my-6">
					<h4 className="text-sm font-medium text-dark-blue mb-2">Notes</h4>
					<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
						<p className="text-sm text-dark-blue/60 leading-relaxed whitespace-pre-line" style={textBreakStyle}>
							{citationData.notes.trim()}
						</p>
					</div>
					<p className="text-xs text-gray-500 mt-1 pl-[10px]">Notes are for your reference only and are not included in the citation.</p>
				</div>
			)}

			{!hasUserData && staticReferenceExample && (
				<div className="my-6">
					<h4 className="text-sm font-medium text-dark-blue mb-2">Reference example</h4>
					<div className="flex pl-[10px] items-start gap-[10px] self-stretch border-l-[1.5px] border-[#E8E8EA] pr-2">
						<p
							className="text-sm text-dark-blue/60 leading-relaxed whitespace-pre-line"
							style={textBreakStyle}
						>
							{staticReferenceExample.replace(/<[^>]*>/g, '')}
						</p>
					</div>
				</div>
			)}

			{!hasUserData && (
				<div className="mt-6 p-3 bg-blue-50 rounded-lg border border-blue-100">
					<p className="text-xs text-dark-blue/70">
						<span className="font-semibold">Note:</span> Enter source details to generate your APA citation
						automatically.
					</p>
				</div>
			)}

			{hasUserData && (
				<div className="mt-6 p-3 bg-green-50 rounded-lg border border-green-100">
					<p className="text-xs text-dark-blue/70">
						<span className="font-semibold">✓ Live preview active.</span> Your citation updates as you type.
						Complete all required fields to finish.
					</p>
				</div>
			)}
		</div>
	);
}
